import { FormEvent, useState } from 'react'

interface Props {
  productName: string
  /** One entry per portion, in cart order. Empty string means no note. */
  notes: string[]
  /**
   * Adisyona kaydedilmiş, mutfağa gitmiş porsiyonların notları (20 Eki 2026).
   * Kilitli gösteriliyor: yenmiş bir porsiyonun notu değiştirilemez, değişse
   * mutfağa yanlış fiş giderdi. `notes` yalnız yeni porsiyonlar.
   */
  sent?: string[]
  /** Açılışta seçili porsiyon (`notes` içindeki sıra). */
  startAt?: number
  /** Tick-box options for this item; may be empty, leaving only free text. */
  options: string[]
  onConfirm: (notes: string[]) => void
  onCancel: () => void
}

/** Splits a stored note back into ticked options and whatever was typed. */
function parseNote(note: string, options: string[]): { picked: string[]; extra: string } {
  const parts = note
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  const picked = options.filter((q) => parts.some((p) => p.localeCompare(q, 'tr') === 0))
  const extra = parts
    .filter((p) => !options.some((q) => p.localeCompare(q, 'tr') === 0))
    .join(', ')

  return { picked, extra }
}

/** Options keep their listed order, so a combination always reads the same. */
function compose(picked: string[], extra: string, options: string[]): string {
  return [...options.filter((q) => picked.includes(q)), extra.trim()].filter(Boolean).join(', ')
}

/**
 * Notes for one product, one portion at a time.
 *
 * Three Adana where the first two are soğansız and the third is not is an
 * everyday order. Numbered tabs across the top select which portion is being
 * described, so each one carries its own note rather than the whole line
 * sharing one.
 */
export function NoteDialog({ productName, notes, sent = [], startAt = 0, options, onConfirm, onCancel }: Props) {
  const [at, setAt] = useState(() => Math.min(Math.max(startAt, 0), Math.max(notes.length - 1, 0)))
  /**
   * Ticked options and free text held apart, one entry per portion.
   *
   * They used to live in the composed string and be re-split on every
   * keystroke. Round-tripping through `compose`/`parseNote` meant the text was
   * trimmed as it was typed, a space could never survive long enough to be
   * followed by another word, and a comma silently became a separator. Kept
   * apart, what is typed is left exactly alone until it is saved.
   */
  const [picks, setPicks] = useState<string[][]>(() =>
    notes.map((n) => parseNote(n, options).picked),
  )
  const [extras, setExtras] = useState<string[]>(() =>
    notes.map((n) => parseNote(n, options).extra),
  )

  // Nothing is focused on open: the checkboxes are the point, and pulling up
  // the keyboard would cover them on a phone.

  const current = { picked: picks[at] ?? [], extra: extras[at] ?? '' }

  const write = (picked: string[], extra: string) => {
    setPicks((p) => p.map((v, i) => (i === at ? picked : v)))
    setExtras((e) => e.map((v, i) => (i === at ? extra : v)))
  }

  const toggle = (label: string) => {
    const next = current.picked.includes(label)
      ? current.picked.filter((c) => c !== label)
      : [...current.picked, label]
    write(next, current.extra)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    // Composed once, on the way out. Trimming here is right: it tidies the
    // finished note rather than fighting the person writing it.
    onConfirm(picks.map((p, i) => compose(p, extras[i] ?? '', options)))
  }

  const hasNote = current.picked.length > 0 || Boolean(current.extra.trim())

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <form className="dlg__card" onSubmit={submit}>
        <h2>{productName}</h2>

        {/* One tab per portion. A tick marks the ones already described, so a
            half-finished order is obvious at a glance. */}
        {sent.length + picks.length > 1 && (
          <div className="portions">
            {/* Gönderilmişler önde, kilitli. */}
            {sent.map((note, i) => (
              <div key={`s${i}`} className="portions__tab is-sent" aria-disabled="true">
                <span className="portions__no">{i + 1}</span>
                <span className="portions__note">Gönderildi{note ? `, ${note}` : ''}</span>
              </div>
            ))}
            {picks.map((_, i) => {
              // Composed here only to be read: the stored halves stay untouched
              // so typing in one portion never reshapes another.
              const label = compose(picks[i] ?? [], extras[i] ?? '', options)
              return (
                <button
                  key={i}
                  type="button"
                  className={`portions__tab ${i === at ? 'on' : ''} ${
                    label ? 'has-note' : ''
                  }`}
                  onClick={() => setAt(i)}
                >
                  <span className="portions__no">{sent.length + i + 1}</span>
                  <span className="portions__note">{label || 'not yok'}</span>
                </button>
              )
            })}
          </div>
        )}

        {picks.length === 0 && (
          <p className="portions__all-sent">
            Bu üründen yeni porsiyon yok; gönderilmiş porsiyonların notu değiştirilemez.
          </p>
        )}

        {picks.length > 0 && (
        <>
        {/* Empty for something like a bottle of water: no tick boxes, but the
            free-text field stays for anything genuinely unusual. */}
        <div className="opts">
          {options.map((label) => (
            <label
              key={label}
              className={`opt ${current.picked.includes(label) ? 'on' : ''}`}
            >
              <input
                type="checkbox"
                checked={current.picked.includes(label)}
                onChange={() => toggle(label)}
              />
              {label}
            </label>
          ))}
        </div>

        <label className="dlg__field">
          Ek not
          <input
            value={current.extra}
            onChange={(e) => write(current.picked, e.target.value)}
            placeholder="Örn. kasadan teslim alınacak"
            maxLength={60}
          />
        </label>
        </>
        )}

        <div className="dlg__buttons">
          {hasNote && (
            <button type="button" className="btn btn--ghost" onClick={() => write([], '')}>
              Temizle
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          {picks.length > 0 && (
            <button type="submit" className="btn btn--primary">
              Tamam
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
